import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import QuickAccess from '../QuickAccess';
import {
  quickAccessConfigs,
  canAccessQuickAccessPage,
  filterQuickAccessPages,
} from '../../config/quickAccessConfig';
import { setCurrentRoomId, setViewedRooms } from '../../redux/room/room.actions';
import { selectPermissions } from '../../redux/user/user.actions';
import { ordemDoAcessoRapido, jaEstaNaOrdem } from '../../utils/ordemDoAcessoRapido';
import {
  alternarGuiaFixada,
  comFixadasPrimeiro,
  ehGuiaFixada,
  gravarGuiasFixadas,
  lerGuiasFixadas,
} from '../../utils/guiasFixadas';
import { colapsarConversas, ehAbaUnicaDeChat, ID_DA_ABA_DE_CHAT } from '../../utils/abaUnicaDeChat';
import { useWebSocket } from '../WebSocketContext/WebSocketContext';

const GLOBAL_ORDER_KEY = 'quickAccessGlobalOrder';

/** Item é aba "Indicadores" (nunca deve aparecer como aba) */
const isIndicadoresTab = (item) =>
  item && (item.path === '/' || item.id === 'indicadores' || item.key === 'indicadores');

/**
 * Componente que unifica todos os tipos de QuickAccess em uma única lista ordenável
 * Permite reordenar itens entre diferentes tipos (tickets, settings, etc.)
 */
const QuickAccessUnified = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const permissions = useSelector(selectPermissions) || [];
  const quickAccessRef = useRef(null);
  /** Ref para bloquear auto-add quando o usuário está fechando a aba da página atual */
  const closingPathRef = useRef(null);
  // Limite desativado: o excedente vai para o menu de overflow (+N) no QuickAccess.
  const [limitReached, setLimitReached] = useState(false);

  // Migracao suave: remove o flag legado de limite atingido.
  useEffect(() => {
    localStorage.removeItem('quickAccessLimitReached');
  }, []);
  
  // Estado para ordem global (remove entradas indevidas de Indicadores)
  const [globalOrder, setGlobalOrder] = useState(() => {
    try {
      const stored = localStorage.getItem(GLOBAL_ORDER_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      // `ordemDoAcessoRapido` tira entrada repetida, que e o que gerava
      // "two children with the same key". Vem antes do filtro do legado para
      // que a limpeza seja gravada de uma vez: quem ja tem a ordem estragada
      // no navegador se conserta no proximo carregamento.
      const arr = ordemDoAcessoRapido(Array.isArray(parsed) ? parsed : []);
      const cleaned = arr.filter(
        (entry) => !(entry.typeId === 'pages' && (entry.itemId === 'indicadores' || entry.itemId === '/'))
      ).filter(
        /*
         * Entradas de CONVERSA individual, do tempo em que cada uma tinha a
         * sua aba (até 19/10/2026). Elas não desenham nada hoje -- a aba única
         * usa um id só --, mas ficariam acumulando na ordem salva, uma por
         * conversa aberta na vida, para sempre.
         */
        (entry) => entry.typeId !== 'rooms' || entry.itemId === ID_DA_ABA_DE_CHAT
      );
      if (arr.length !== (Array.isArray(parsed) ? parsed.length : 0)) {
        try {
          localStorage.setItem(GLOBAL_ORDER_KEY, JSON.stringify(cleaned));
        } catch {
          // ignore
        }
        return cleaned;
      }
      if (cleaned.length !== arr.length) {
        try {
          localStorage.setItem(GLOBAL_ORDER_KEY, JSON.stringify(cleaned));
        } catch {
          // ignore
        }
        return cleaned;
      }
      return arr;
    } catch {
      return [];
    }
  });
  
  // Flag para controlar se já inicializou a ordem
  // Se já há ordem salva, marca como inicializado desde o início
  const [orderInitialized, setOrderInitialized] = useState(() => {
    try {
      const stored = localStorage.getItem(GLOBAL_ORDER_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) && parsed.length > 0;
      }
    } catch {
      // Ignora erros
    }
    return false;
  });

  // Obtém itens de cada tipo usando hooks do Redux
  // Usa useSelector com comparação customizada para evitar re-renders desnecessários
  const reduxItems = useSelector(state => {
    const items = {};
    quickAccessConfigs.forEach(config => {
      if (config.storageType === 'redux' && config.reduxSelector) {
        const result = config.reduxSelector(state);
        items[config.id] = Array.isArray(result) ? result : [];
      }
    });
    return items;
  }, (prev, next) => {
    // Comparação customizada do React Redux:
    // Retorna true se são iguais (NÃO deve re-renderizar)
    // Retorna false se são diferentes (DEVE re-renderizar)
    for (const config of quickAccessConfigs) {
      if (config.storageType === 'redux' && config.reduxSelector) {
        const prevItems = prev[config.id] || [];
        const nextItems = next[config.id] || [];
        
        // Se o tamanho mudou, definitivamente mudou
        if (prevItems.length !== nextItems.length) {
          return false; // Mudou, deve re-renderizar
        }
        
        // Compara IDs dos itens para ver se são os mesmos
        const prevIds = prevItems.map(item => {
          return config.id === 'rooms' && item.room_id ? item.room_id : (item.id || item.key);
        }).sort();
        
        const nextIds = nextItems.map(item => {
          return config.id === 'rooms' && item.room_id ? item.room_id : (item.id || item.key);
        }).sort();
        
        // Se os IDs são diferentes, mudou
        if (JSON.stringify(prevIds) !== JSON.stringify(nextIds)) {
          return false; // Mudou, deve re-renderizar
        }
      }
    }
    // Se chegou aqui, nada mudou
    return true; // Iguais, NÃO deve re-renderizar
  });

  // Obtém itens de localStorage e state (remove Indicadores de quickAccessPages se persistido indevidamente)
  const [localItems, setLocalItems] = useState(() => {
    const items = {};
    quickAccessConfigs.forEach(config => {
      if (config.storageType === 'localStorage' && config.storageKey) {
        try {
          const stored = localStorage.getItem(config.storageKey);
          const parsed = stored ? JSON.parse(stored) : [];
          const arr = Array.isArray(parsed) ? parsed : [];
          let cleaned = config.id === 'pages' ? arr.filter((i) => !isIndicadoresTab(i)) : arr;
          if (config.id === 'pages') {
            cleaned = filterQuickAccessPages(cleaned, permissions);
          }
          if (cleaned.length !== arr.length && config.storageKey) {
            try {
              localStorage.setItem(config.storageKey, JSON.stringify(cleaned));
            } catch {
              // ignore
            }
          }
          items[config.id] = cleaned;
        } catch {
          items[config.id] = [];
        }
      } else if (config.storageType === 'state') {
        const initial = config.initialItems || [];
        items[config.id] = Array.isArray(initial) ? initial : [];
      }
    });
    return items;
  });

  // Recarrega a aba do Orby quando ela é fixada (ou removida) pelo botão dentro da
  // janela do Orby — o evento vem de fora deste componente.
  useEffect(() => {
    const reload = () => {
      try {
        const stored = localStorage.getItem('quickAccessOrby');
        const parsed = stored ? JSON.parse(stored) : [];
        setLocalItems((prev) => ({ ...prev, orby: Array.isArray(parsed) ? parsed : [] }));
      } catch {
        setLocalItems((prev) => ({ ...prev, orby: [] }));
      }
    };
    window.addEventListener('quickAccessOrbyChanged', reload);
    return () => window.removeEventListener('quickAccessOrbyChanged', reload);
  }, []);

  // Função auxiliar para obter ID do item (suporta diferentes formatos)
  const getItemId = useCallback((item, config) => {
    // Para rooms, usa room_id
    if (config && config.id === 'rooms' && item.room_id) {
      return item.room_id;
    }
    // Para outros tipos, usa id ou key
    return item.id || item.key;
  }, []);

  // Combina todos os itens e garante que todos sejam arrays
  const itemsByType = useMemo(() => {
    const combined = { ...reduxItems, ...localItems };
    // Garante que todos os valores sejam arrays
    Object.keys(combined).forEach(key => {
      if (!Array.isArray(combined[key])) {
        combined[key] = [];
      }
    });
    return combined;
  }, [reduxItems, localItems]);

  /*
   * ## As conversas colapsadas em UMA aba (pedido da Gisele, 19/10/2026)
   *
   * `itemsByTypeExibidos` é o que a barra desenha; `itemsByType` continua
   * sendo a verdade (as conversas, uma a uma), e a distinção não é estética:
   *
   * - `clearRoomNewFlag` reescreve `viewedRooms` no Redux. Se ele lesse a
   *   lista colapsada, gravaria a aba sintética NO LUGAR das conversas do
   *   operador -- e elas sumiriam do acesso rápido.
   * - `handleRemoveItem`, ao contrário, deve ler a colapsada: fechar a aba
   *   "Chat" é fechar todas as conversas, que é o que o X ali significa.
   *
   * A conta de novidades vem do `newMessageChats`, o mesmo sinal vivo que já
   * fazia a aba individual piscar.
   */
  const { newMessageChats } = useWebSocket();
  /*
   * A aba "Chat" de PÁGINA sai da barra enquanto a agregada existe.
   *
   * As duas levam ao mesmo lugar (`/chat`) e se chamavam igual: a barra ficava
   * com duas abas "Chat" lado a lado, uma delas inútil. Visto na tela em
   * 19/10, e é o oposto do pedido ("guia única").
   *
   * Some a de página, e não a agregada, porque é a agregada que carrega a
   * conta e o piscar. Sem conversa nenhuma, a de página volta -- e é ela que
   * garante que o operador tenha como voltar ao chat.
   */
  const itemsByTypeExibidos = useMemo(() => {
    const conversas = colapsarConversas(itemsByType.rooms, newMessageChats);
    const paginas = Array.isArray(itemsByType.pages) ? itemsByType.pages : [];

    return {
      ...itemsByType,
      rooms: conversas,
      pages: conversas.length > 0 ? paginas.filter((p) => p?.path !== '/chat') : paginas,
    };
  }, [itemsByType, newMessageChats]);

  /*
   * A aba de página escondida acima, guardada para o `handleReorder`: ele
   * regrava a lista de páginas no `localStorage` a partir do que está na
   * barra, e sem ela o arrastar APAGARIA a aba escondida do armazenamento --
   * ela não voltaria ao fechar as conversas.
   */
  const paginaDeChatOcultaRef = React.useRef(null);
  paginaDeChatOcultaRef.current = (
    (itemsByTypeExibidos.pages || []).length !== (itemsByType.pages || []).length
      ? (itemsByType.pages || []).find((p) => p?.path === '/chat') || null
      : null
  );

  // Inicializa ordem global na primeira vez que há itens (apenas se não houver ordem salva)
  // Usa ref para evitar loops infinitos e garantir que só execute uma vez
  const initializationAttemptedRef = React.useRef(false);
  const itemsByTypeRef = React.useRef(itemsByTypeExibidos);
  const globalOrderRef = React.useRef(globalOrder);
  
  // Atualiza refs quando valores mudam
  useEffect(() => {
    itemsByTypeRef.current = itemsByTypeExibidos;
  }, [itemsByTypeExibidos]);
  
  useEffect(() => {
    globalOrderRef.current = globalOrder;
  }, [globalOrder]);
  
  useEffect(() => {
    // Só tenta inicializar uma vez e se não estiver inicializado
    if (orderInitialized || initializationAttemptedRef.current) {
      return;
    }
    
    // Se já há ordem salva, não precisa inicializar
    if (globalOrderRef.current.length > 0) {
      setOrderInitialized(true);
      initializationAttemptedRef.current = true;
      return;
    }
    
    // Verifica se há itens para criar ordem inicial
    const hasItems = Object.values(itemsByTypeRef.current).some(items => 
      Array.isArray(items) && items.length > 0
    );
    
    if (hasItems) {
      initializationAttemptedRef.current = true;
      
      // Usa setTimeout para processar de forma assíncrona e evitar loop
      setTimeout(() => {
        const allItems = [];
        quickAccessConfigs.forEach(config => {
          const typeItems = Array.isArray(itemsByTypeRef.current[config.id]) 
            ? itemsByTypeRef.current[config.id] 
            : [];
          typeItems.forEach(item => {
            const itemId = getItemId(item, config);
            allItems.push({ typeId: config.id, itemId });
          });
        });
        
        if (allItems.length > 0) {
          setGlobalOrder(allItems);
          localStorage.setItem(GLOBAL_ORDER_KEY, JSON.stringify(allItems));
        }
        setOrderInitialized(true);
      }, 100);
    }
  }, [orderInitialized, getItemId]);

  // Cria lista unificada com metadata de tipo
  const unifiedItems = useMemo(() => {
    const items = [];

      // Se há ordem global salva, usa ela.
      //
      // `ordemDoAcessoRapido` e a GARANTIA de que a mesma aba nao e desenhada
      // duas vezes: as duas pontas que criavam a entrada repetida foram
      // consertadas, mas seis lugares escrevem nesta ordem e basta um deles
      // voltar a duplicar para o aviso de `key` voltar com ele.
      if (globalOrder.length > 0) {
        ordemDoAcessoRapido(globalOrder).forEach(orderEntry => {
          const config = quickAccessConfigs.find(c => c.id === orderEntry.typeId);
          if (config) {
            const typeItems = Array.isArray(itemsByTypeExibidos[orderEntry.typeId]) 
              ? itemsByTypeExibidos[orderEntry.typeId] 
              : [];
            const item = typeItems.find(i => getItemId(i, config) === orderEntry.itemId);
            if (item) {
              const normalizedItem = {
                ...item,
                __quickAccessType: orderEntry.typeId,
                __quickAccessConfig: config,
              };
              // Normaliza ID: para rooms usa room_id como id temporário
              if (config.id === 'rooms' && item.room_id && !normalizedItem.id) {
                normalizedItem.id = item.room_id;
              }
              items.push(normalizedItem);
            }
          }
        });
        
        // Adiciona itens novos que não estão na ordem global
        quickAccessConfigs.forEach(config => {
          const typeItems = Array.isArray(itemsByTypeExibidos[config.id]) 
            ? itemsByTypeExibidos[config.id] 
            : [];
          typeItems.forEach(item => {
            const itemId = getItemId(item, config);
            const alreadyInOrder = globalOrder.some(
              entry => entry.typeId === config.id && entry.itemId === itemId
            );
            if (!alreadyInOrder) {
              const normalizedItem = {
                ...item,
                __quickAccessType: config.id,
                __quickAccessConfig: config,
              };
              // Normaliza ID: para rooms usa room_id como id temporário
              if (config.id === 'rooms' && item.room_id && !normalizedItem.id) {
                normalizedItem.id = item.room_id;
              }
              items.push(normalizedItem);
            }
          });
        });
      } else {
        // Primeira vez: cria ordem baseada na ordem dos tipos e itens
        quickAccessConfigs.forEach(config => {
          const typeItems = Array.isArray(itemsByTypeExibidos[config.id]) 
            ? itemsByTypeExibidos[config.id] 
            : [];
          typeItems.forEach(item => {
            const normalizedItem = {
              ...item,
              __quickAccessType: config.id,
              __quickAccessConfig: config,
            };
            // Normaliza ID: para rooms usa room_id como id temporário
            if (config.id === 'rooms' && item.room_id && !normalizedItem.id) {
              normalizedItem.id = item.room_id;
            }
            items.push(normalizedItem);
          });
        });
        
      }
    
    return items;
  }, [itemsByTypeExibidos, globalOrder, getItemId]);

  /**
   * AS GUIAS FIXADAS (Gisele: "fixar as guias mais utilizadas").
   *
   * Desenho do dono do produto em 03/11/2026: alfinete ao passar o mouse,
   * clica e fixa, por usuário.
   *
   * ## O que fixar resolve
   *
   * A barra não apaga aba nenhuma, mas tem largura: o que não cabe vai para o
   * menu de excedente (`+N`). Fixar é dizer "esta não desce" -- a guia fixada
   * vem sempre primeiro, e por isso nunca cai no `+N`.
   *
   * ## A ordem arrastada continua valendo
   *
   * `comFixadasPrimeiro` é ESTÁVEL: ela reposiciona as fixadas e preserva a
   * ordem dentro de cada grupo. A consequência que vale saber: arrastar uma
   * aba comum para antes de uma fixada não a segura ali -- ela volta para
   * depois das fixadas no próximo render. É a mesma regra da base de
   * conhecimento (fixado primeiro, depois a ordem de sempre), e é o que
   * impede que arrastar desfaça o que o alfinete fez.
   */
  const [fixadas, setFixadas] = useState(lerGuiasFixadas);

  /** O par (tipo, item) de uma aba -- a mesma identidade da ordem global. */
  const identificarAba = useCallback((item) => {
    const config = item && item.__quickAccessConfig;

    return {
      typeId: item && item.__quickAccessType,
      itemId: config ? getItemId(item, config) : undefined,
    };
  }, [getItemId]);

  const estaFixada = useCallback((item) => {
    const { typeId, itemId } = identificarAba(item);

    return ehGuiaFixada(fixadas, typeId, itemId);
  }, [fixadas, identificarAba]);

  const alternarFixacao = useCallback((item) => {
    const { typeId, itemId } = identificarAba(item);

    if (typeId === undefined || itemId === undefined) return;

    setFixadas((atuais) => gravarGuiasFixadas(alternarGuiaFixada(atuais, typeId, itemId)));
  }, [identificarAba]);

  const itensComFixadasPrimeiro = useMemo(
    () => comFixadasPrimeiro(unifiedItems, fixadas, identificarAba),
    [unifiedItems, fixadas, identificarAba]
  );

  // Sempre permite adicionar; o excedente vai para o menu de overflow (+N).
  const checkLimitBeforeAdd = useCallback(() => true, []);

  // Auto-add para tipos localStorage (Páginas)
  useEffect(() => {
    if (closingPathRef.current && closingPathRef.current === location.pathname) {
      closingPathRef.current = null;
      return;
    }
    quickAccessConfigs.forEach(config => {
      // `aceitaCaminho` é uma FUNÇÃO, e não mais uma expressão regular: ela e
      // o `getItem` saem do mesmo catálogo de páginas, então não há como uma
      // aceitar um caminho que a outra não sabe transformar em aba (era assim
      // que páginas deixavam de entrar no Acesso Rápido, sem erro nenhum).
      if (config.autoAdd && config.autoAdd.aceitaCaminho && config.storageType === 'localStorage') {
        const matches = config.autoAdd.aceitaCaminho(location.pathname);
        if (matches) {
          const newItem = config.autoAdd.getItem ? config.autoAdd.getItem(location.pathname) : null;
          if (newItem && canAccessQuickAccessPage(newItem.path, permissions)) {
            // Obtém itens atuais do localStorage diretamente
            let currentItems = [];
            try {
              const stored = localStorage.getItem(config.storageKey);
              currentItems = stored ? JSON.parse(stored) : [];
            } catch {
              currentItems = [];
            }
            
            // REGRA 1: Verifica se a aba já existe PRIMEIRO
            const itemExists = currentItems.some(i => {
              const itemKey = i.id || i.key;
              const newKey = newItem.id || newItem.key;
              return itemKey === newKey;
            });
            
            // Se já existe, apenas retorna (não altera ordem, não remove nada)
            if (itemExists) {
              return;
            }
            
            // REGRA 2: Se não existe, verifica limite ANTES de adicionar
            if (!checkLimitBeforeAdd()) {
              return;
            }
            
            // REGRA 3: Se passou na verificação, adiciona normalmente
            // Não remove nenhum item existente, apenas adiciona o novo
            const updated = Array.isArray(currentItems) ? [...currentItems, newItem] : [newItem];
            localStorage.setItem(config.storageKey, JSON.stringify(updated));
            // Atualiza estado local
            setLocalItems(prev => ({ ...prev, [config.id]: updated }));
            // Atualiza a ordem global adicionando o novo item no final.
            //
            // O append e IDEMPOTENTE: a REGRA 1 acima olha a lista de paginas,
            // no outro endereco do localStorage, e nao a ordem -- as duas
            // podem dessincronizar. A inicializacao da ordem roda num
            // `setTimeout(..., 100)` e SOBRESCREVE tudo; se a sobrescrita
            // chegasse depois deste append, a entrada ficava duas vezes e a
            // barra desenhava a mesma aba com a mesma `key`.
            setGlobalOrder(prev => {
              const itemId = newItem.id || newItem.key;
              if (jaEstaNaOrdem(prev, config.id, itemId)) return prev;

              const newOrder = [...prev, { typeId: config.id, itemId }];
              localStorage.setItem(GLOBAL_ORDER_KEY, JSON.stringify(newOrder));
              return newOrder;
            });
          }
        }
      }
    });
  }, [location.pathname, checkLimitBeforeAdd, permissions]);

  // Remove abas de páginas sem permissão quando o perfil mudar
  useEffect(() => {
    const pagesConfig = quickAccessConfigs.find((c) => c.id === 'pages');
    if (!pagesConfig?.storageKey) return;
    try {
      const stored = localStorage.getItem(pagesConfig.storageKey);
      const parsed = stored ? JSON.parse(stored) : [];
      const arr = Array.isArray(parsed) ? parsed : [];
      const cleaned = filterQuickAccessPages(
        arr.filter((i) => !isIndicadoresTab(i)),
        permissions
      );
      if (cleaned.length !== arr.length) {
        localStorage.setItem(pagesConfig.storageKey, JSON.stringify(cleaned));
        setLocalItems((prev) => ({ ...prev, pages: cleaned }));
        setGlobalOrder((prev) => {
          const allowedIds = new Set(cleaned.map((i) => i.id || i.key));
          const next = prev.filter(
            (entry) => entry.typeId !== 'pages' || allowedIds.has(entry.itemId)
          );
          localStorage.setItem(GLOBAL_ORDER_KEY, JSON.stringify(next));
          return next;
        });
      }
    } catch {
      // ignore
    }
  }, [permissions]);

  // NOTA: Removida a adição automática de novos itens à ordem global para evitar loops infinitos
  // Os novos itens aparecerão automaticamente no unifiedItems quando não estiverem na ordem global
  // E serão adicionados à ordem global apenas quando o usuário reordenar manualmente

  // Função para obter label do item (usa a config do tipo)
  const getItemLabel = useCallback((item) => {
    const config = item.__quickAccessConfig;
    return config?.getItemLabel ? config.getItemLabel(item) : String(item);
  }, []);

  // Função para obter path do item
  const getItemPath = useCallback((item) => {
    const config = item.__quickAccessConfig;
    return config?.getItemPath ? config.getItemPath(item) : null;
  }, []);

  // Função para obter tooltip do item
  const getItemTooltip = useCallback((item) => {
    const config = item.__quickAccessConfig;
    return config?.getItemTooltip ? config.getItemTooltip(item) : null;
  }, []);

  // Item 8: ao dar foco numa aba de chat recém-atribuído, remove o flag __isNew
  // (a aba para de piscar). Atualiza os viewedRooms no Redux.
  const clearRoomNewFlag = useCallback((roomId) => {
    const rooms = Array.isArray(itemsByType.rooms) ? itemsByType.rooms : [];
    let changed = false;
    const updated = rooms.map((r) => {
      if (String(r.room_id) === String(roomId) && r.__isNew) {
        changed = true;
        const { __isNew, ...rest } = r;
        return rest;
      }
      return r;
    });
    if (changed) {
      dispatch(setViewedRooms(updated));
    }
  }, [itemsByType, dispatch]);

  // Função customizada para clique em item
  const handleItemClick = useCallback((item) => {
    const config = item.__quickAccessConfig;

    /*
     * A aba única de chat abre a PÁGINA de chat, com a lista de hoje (decisão
     * do dono do produto em 19/10), e não uma conversa: nada de
     * `setCurrentRoomId`, que receberia o id sintético da aba.
     *
     * O piscar para por consequência -- ler a conversa é que apaga o sinal --,
     * então aqui não há sinalizador a limpar.
     */
    if (ehAbaUnicaDeChat(item)) {
      history.push('/chat');
      return;
    }

    // Se for uma room, define o currentRoomId e navega
    if (config.id === 'rooms' && item.room_id) {
      clearRoomNewFlag(item.room_id);
      dispatch(setCurrentRoomId(item.room_id));
      history.push(`/chat?roomId=${item.room_id}`); // pagina de chat (antes ia p/ "/", o dashboard)
    } else if (config.onItemClick) {
      // Usa callback customizado se existir
      config.onItemClick(item);
    } else if (config.getItemPath) {
      // Navegação padrão
      const path = config.getItemPath(item);
      if (path) {
        history.push(path);
      }
    }
  }, [dispatch, history, clearRoomNewFlag]);

  // Item 8: se o operador abrir o chat por outro caminho (ex.: lista de salas), a URL
  // vira /chat?roomId=X — para de piscar a aba correspondente também nesse caso.
  useEffect(() => {
    if (location.pathname !== '/chat') return;
    const roomId = new URLSearchParams(location.search).get('roomId');
    if (roomId) {
      clearRoomNewFlag(roomId);
    }
  }, [location.pathname, location.search, clearRoomNewFlag]);

  const handleRemoveItem = useCallback((item) => {
    const typeId = item.__quickAccessType;
    const config = item.__quickAccessConfig;
    const itemId = (config.id === 'rooms' && item.room_id) ? item.room_id : (item.id || item.key);

    const currentPath = location.pathname;
    const currentSearch = location.search;
    const currentFullPath = `${currentPath}${currentSearch || ''}`;
    const itemPath = getItemPath(item);
    
    let isCurrentItem = false;
    if (ehAbaUnicaDeChat(item)) {
      // Fechar a aba de chat estando na tela de chat tem de tirar o operador
      // de lá, como acontece com qualquer outra aba.
      isCurrentItem = currentPath === '/chat';
    } else if (config.id === 'rooms' && item.room_id) {
      const urlParams = new URLSearchParams(currentSearch);
      const currentRoomId = urlParams.get('roomId');
      isCurrentItem = currentPath === '/chat' && currentRoomId === String(item.room_id);
    } else if (itemPath) {
      isCurrentItem = currentFullPath === itemPath || currentPath === itemPath;
    }

    if (typeId === 'tickets' && item?.isDraft && item?.draftId) {
      try {
        const key = `ticketDraft:${item.draftId}`;
        const discardedKey = `ticketDraftDiscarded:${item.draftId}`;
        const ok = window.confirm('O ticket que você está abrindo ainda não foi salvo. Se você sair, as informações serão perdidas. Deseja continuar?');
        if (!ok) {
          return;
        }
        try {
          sessionStorage.setItem('quickAccessDraftClose', JSON.stringify({ draftId: String(item.draftId), ts: Date.now() }));
        } catch {}
        localStorage.setItem(discardedKey, '1');
        localStorage.removeItem(key);
      } catch {
      }
    }

    if (isCurrentItem && typeId === 'pages' && itemPath) {
      closingPathRef.current = itemPath;
    }

    let itemToNavigate = null;
    if (isCurrentItem) {
      const currentIndex = unifiedItems.findIndex(i => {
        const iId = (i.__quickAccessConfig?.id === 'rooms' && i.room_id) ? i.room_id : (i.id || i.key);
        return iId === itemId;
      });

      if (currentIndex !== -1) {
        if (currentIndex > 0) {
          itemToNavigate = unifiedItems[currentIndex - 1];
        } 
        else if (currentIndex < unifiedItems.length - 1) {
          itemToNavigate = unifiedItems[currentIndex + 1];
        }
      }
    }

    setGlobalOrder(prev => {
      const updated = prev.filter(entry => !(entry.typeId === typeId && entry.itemId === itemId));
      localStorage.setItem(GLOBAL_ORDER_KEY, JSON.stringify(updated));
      return updated;
    });

    if (config.storageType === 'redux' && config.reduxAction) {
      // A lista EXIBIDA: fechar a aba "Chat" fecha todas as conversas, que é
      // o que o X daquela aba significa agora (ela representa o conjunto).
      const currentItems = Array.isArray(itemsByTypeExibidos[typeId]) ? itemsByTypeExibidos[typeId] : [];
      const updated = currentItems.filter(i => {
        const currentKey = (config.id === 'rooms' && i.room_id) ? i.room_id : (i.id || i.key);
        return currentKey !== itemId;
      });
      dispatch(config.reduxAction(updated));
    } else if (config.storageType === 'localStorage' && config.storageKey) {
      let currentItems = [];
      try {
        const stored = localStorage.getItem(config.storageKey);
        currentItems = stored ? JSON.parse(stored) : [];
      } catch {
        currentItems = [];
      }
      
      const updated = currentItems.filter(i => {
        const currentKey = (config.id === 'rooms' && i.room_id) ? i.room_id : (i.id || i.key);
        return currentKey !== itemId;
      });
      
      localStorage.setItem(config.storageKey, JSON.stringify(updated));
      setLocalItems(prev => {
        const newState = { ...prev, [typeId]: updated };
        return newState;
      });
    }

    if (config.onItemRemove) {
      config.onItemRemove(item);
    }

    if (isCurrentItem) {
      const delay = config.storageType === 'localStorage' ? 150 : 100;
      setTimeout(() => {
        if (itemToNavigate) {
          handleItemClick(itemToNavigate);
        } else {
          history.push('/');
        }
      }, delay);
    }
    
    setTimeout(() => {
      if (quickAccessRef.current && limitReached) {
        if (quickAccessRef.current.canAddItem()) {
          setLimitReached(false);
          localStorage.removeItem('quickAccessLimitReached');
        }
      }
    }, 100);
  }, [itemsByTypeExibidos, dispatch, limitReached, location.pathname, location.search, unifiedItems, getItemPath, handleItemClick, history]);

  useEffect(() => {
    if (unifiedItems.length === 0 && limitReached) {
      setLimitReached(false);
      localStorage.removeItem('quickAccessLimitReached');
    }
  }, [unifiedItems.length, limitReached]);

  // Identifica a aba (item do acesso rápido) correspondente à rota atual.
  const isActiveUnifiedItem = useCallback((item) => {
    const config = item.__quickAccessConfig;
    const currentPath = location.pathname;
    const currentSearch = location.search;
    /*
     * A aba única está ativa em QUALQUER lugar da página de chat, com conversa
     * aberta ou não: ela representa o conjunto. É também o que faz ela parar
     * de piscar enquanto o operador está ali -- as conversas com mensagem nova
     * aparecem na lista daquela tela, com o aviso delas.
     */
    if (ehAbaUnicaDeChat(item)) return currentPath === '/chat';

    if (config?.id === 'rooms' && item.room_id) {
      const roomId = new URLSearchParams(currentSearch).get('roomId');
      return currentPath === '/chat' && roomId === String(item.room_id);
    }
    const itemPath = config?.getItemPath ? config.getItemPath(item) : null;
    if (!itemPath) return false;
    if (itemPath === `${currentPath}${currentSearch || ''}` || itemPath === currentPath) return true;
    if ((config?.id === 'tickets' || item.id) && item.id && currentPath.startsWith('/ticket/')) {
      return currentPath.replace('/ticket/', '') === String(item.id);
    }
    return false;
  }, [location.pathname, location.search]);

  // Atalhos de teclado estilo Movidesk: Alt+T abre novo ticket, Alt+W fecha a aba atual.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      const key = (e.key || '').toLowerCase();
      if (key === 't') {
        e.preventDefault();
        history.push('/ticket/new');
      } else if (key === 'w') {
        const active = unifiedItems.find((item) => isActiveUnifiedItem(item));
        if (active) {
          e.preventDefault();
          handleRemoveItem(active);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [unifiedItems, isActiveUnifiedItem, handleRemoveItem, history]);

  useEffect(() => {
    if (!limitReached || !quickAccessRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (quickAccessRef.current) {
        if (quickAccessRef.current.canAddItem()) {
          setLimitReached(false);
          localStorage.removeItem('quickAccessLimitReached');
        }
      }
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [unifiedItems.length, limitReached]);

  // Função para reordenar itens globalmente
  const handleReorder = useCallback((reorderedItems) => {
    // Atualiza ordem global
    const newOrder = reorderedItems.map(item => {
      const config = item.__quickAccessConfig;
      const itemId = (config.id === 'rooms' && item.room_id) ? item.room_id : (item.id || item.key);
      return {
        typeId: item.__quickAccessType,
        itemId,
      };
    });
    
    setGlobalOrder(newOrder);
    localStorage.setItem(GLOBAL_ORDER_KEY, JSON.stringify(newOrder));

    // Atualiza storage de cada tipo com a nova ordem (apenas itens daquele tipo)
    quickAccessConfigs.forEach(config => {
      const typeItems = reorderedItems
        .filter(item => item.__quickAccessType === config.id)
        .map(item => {
          // Remove metadata antes de salvar
          const { __quickAccessType, __quickAccessConfig, ...cleanItem } = item;
          return cleanItem;
        });

      // A aba "Chat" de página está escondida da barra enquanto houver
      // conversas: ela não vem em `reorderedItems`, e gravar sem ela a
      // apagaria do armazenamento para sempre.
      if (config.id === 'pages' && paginaDeChatOcultaRef.current) {
        typeItems.push(paginaDeChatOcultaRef.current);
      }

      /*
       * A aba única de chat NÃO são as conversas: gravá-la em `viewedRooms`
       * apagaria a lista de verdade do operador -- ele arrastaria a aba de
       * lugar e perderia as conversas abertas. A posição dela já foi para
       * `globalOrder`, logo acima, que é onde a ordem mora.
       */
      if (config.id === 'rooms' && typeItems.some(ehAbaUnicaDeChat)) {
        return;
      }

      if (typeItems.length > 0) {
        if (config.storageType === 'redux' && config.reduxAction) {
          dispatch(config.reduxAction(typeItems));
        } else if (config.storageType === 'localStorage' && config.storageKey) {
          localStorage.setItem(config.storageKey, JSON.stringify(typeItems));
        }

        if (config.onReorder) {
          config.onReorder(typeItems);
        }
      }
    });
  }, [dispatch]);

  // No-op: o overflow (+N) substitui o bloqueio por limite.
  const handleLimitReached = useCallback(() => {}, []);


  if (unifiedItems.length === 0) {
    return null;
  }

  return (
    <QuickAccess
      ref={quickAccessRef}
      items={itensComFixadasPrimeiro}
      onItemClick={handleItemClick}
      onItemRemove={handleRemoveItem}
      onReorder={handleReorder}
      getItemLabel={getItemLabel}
      getItemTooltip={getItemTooltip}
      getItemPath={getItemPath}
      maxLabelLength={30}
      enableDrag={true}
      onLimitReached={handleLimitReached}
      estaFixada={estaFixada}
      onTogglePin={alternarFixacao}
    />
  );
};

export default QuickAccessUnified;

